import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
  IconButton,
} from '@material-ui/core';
import { Form } from '@unform/web';
import { assign, includes, isEmpty, isArray } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner, Progress } from '~/components';
import { Input, Select, CheckboxGroup, Hidden } from '~/components/Form';
import {
  ALARME_DESEMPENHOFROTAFORAMEDIA,
  ALARME_DESEMPENHOMOTORISTAFORAMEDIA,
  ALARME_DIVERGENCIARECEBIMENTO,
  ALARME_DIVERGENCIATRANSFERENCIACOMBOIO,
  ALARME_DIVERGENCIATURNO,
  ALARME_VAZAONAOAUTORIZADA,
  ALARME_PAUSAS_DBIC,
} from '~/constants/default';
import { t, validateEmail } from '~/helpers';
import { updateData } from '~/store/modules/alarmeSistema/actions';

import * as S from './styles';

export default function CreateOrUpdate({
  reference,
  data,
  toggleLoading,
  toggleConfirmButton,
}) {
  const dispatch = useDispatch();

  const [listMails, setListMails] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const loadingErrorMessage = 's.geral.loadingerror';

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.alarmeSistema);
  const [userPosto, setUserPosto] = useState();

  const initials = assign(find, data);

  const optionsSN = useMemo(
    () => [
      { label: t('s.geral.opcoes.nao'), value: 'N' },
      { label: t('s.geral.opcoes.sim'), value: 'S' },
    ],
    []
  );

  const optionsVP = useMemo(
    () => [
      { label: t('s.geral.opcoes.valor'), value: 'V' },
      { label: t('s.geral.opcoes.percentual'), value: 'P' },
    ],
    []
  );

  const isVisible = useMemo(
    () =>
      includes(
        [
          ALARME_DESEMPENHOFROTAFORAMEDIA,
          ALARME_DESEMPENHOMOTORISTAFORAMEDIA,
          ALARME_DIVERGENCIARECEBIMENTO,
          ALARME_DIVERGENCIATRANSFERENCIACOMBOIO,
          ALARME_DIVERGENCIATURNO,
          ALARME_VAZAONAOAUTORIZADA,
          ALARME_PAUSAS_DBIC,
        ],
        initials.idalarmeconfig
      ),
    [initials]
  );

  const handleAsyncLoadingChange = useCallback((isLoading) => {
    setPendingRequests((state) => {
      if (hasLoadingError) return 0;

      if (isLoading) return state + 1;

      return state > 0 ? state - 1 : 0;
    });
  }, [hasLoadingError]);

  const handleAsyncLoadingError = useCallback(() => {
    setHasLoadingError(true);
    setPendingRequests(0);
  }, []);

  const handleSubmit = useCallback(
    async (param) => {
      try {
        if (hasLoadingError) {
          toast.error(t(loadingErrorMessage));
          return;
        }

        if (pendingRequests > 0) {
          toast.warning(t('s.geral.validacao.aguardecarregamento'));
          return;
        }

        const schema = Yup.object().shape({
          geraalarmeconfigcliente: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          enviaemailalarmeconfigcliente: Yup.string().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          tipocalculoalarmeconfig: isVisible
            ? Yup.string().required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string().nullable(),
          valorabsolutoalarmeconfig: isVisible
            ? initials.idalarmeconfig === ALARME_PAUSAS_DBIC
              ? Yup.number()
                  .typeError(t('s.geral.validacao.campoobrigatorio'))
                  .required(t('s.geral.validacao.campoobrigatorio'))
                  .integer(t('s.geral.validacao.campoinvalido'))
                  .min(
                    1,
                    t('s.geral.validacao.valorminimo').replace('%s', 1)
                  )
              : Yup.number()
                  .typeError(t('s.geral.validacao.campoobrigatorio'))
                  .required(t('s.geral.validacao.campoobrigatorio'))
                  .min(
                    initials.idalarmeconfig !== 12 ? 0.1 : 0,
                    t('s.geral.validacao.valorminimo').replace(
                      '%s',
                      initials.idalarmeconfig !== 12 ? 0.1 : 0
                    )
                  )
            : Yup.string().nullable(),
          emailsalarmeconfig: Yup.string().nullable(),
        });
        await schema.validate(param, {
          abortEarly: false,
        });

        if (
          param.emailsalarmeconfig != null &&
          param.emailsalarmeconfig.length > 0
        ) {
          const errors = [];

          param.emailsalarmeconfig.split('\n').forEach((email) => {
            if (!validateEmail(email))
              errors.push(
                t('s.geral.validacao.emailinvalido').replace('%s', email)
              );
          });
          if (!isEmpty(errors)) throw errors;
        }

        const objSend = {
          ...initials,
          ...param,
          listaidsusuariosalarmeconfigcliente: userPosto,
          idcliente: client,
        };
        dispatch(updateData(objSend, params));
      } catch (err) {
        const validationErrors = {};
        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });
          reference.current.setErrors(validationErrors);
        } else if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    },
    [
      initials,
      client,
      isVisible,
      listMails,
      userPosto,
      hasLoadingError,
      pendingRequests,
    ]
  );

  useEffect(() => {
    const { listaidsusuariosalarmeconfigcliente } = find;

    if (listaidsusuariosalarmeconfigcliente) {
      setUserPosto(listaidsusuariosalarmeconfigcliente);
    }
  }, [find]);

  useEffect(() => {
    toggleLoading(pendingRequests > 0);
  }, [pendingRequests, toggleLoading]);

  useEffect(() => {
    toggleConfirmButton(!hasLoadingError);
  }, [hasLoadingError, toggleConfirmButton]);

  useEffect(
    () => () => {
      toggleLoading(false);
      toggleConfirmButton(true);
    },
    [toggleLoading, toggleConfirmButton]
  );

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : hasLoadingError ? (
        <div
          style={{
            border: '1px solid #f5c6cb',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            padding: '16px',
            lineHeight: 1.5,
          }}
        >
          {t(loadingErrorMessage)}
        </div>
      ) : (
        <div style={{ position: 'relative', minHeight: '14rem' }}>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Input
              disabled
              name="_"
              label={t('s.configuracao.alarme.codigolabelsistema')}
              value={t(initials.codigolabelsistema)}
            />

            <Select
              name="geraalarmeconfigcliente"
              label={t('s.configuracao.alarme.geraalarmeconfigcliente')}
              options={optionsSN}
            />

            <Select
              name="enviaemailalarmeconfigcliente"
              label={t('s.configuracao.alarme.enviaemailalarmeconfigcliente')}
              options={optionsSN}
              getValue={(value) => setListMails(value === 'S')}
            />

            {listMails && (
              <Input
                multiline
                name="emailsalarmeconfig"
                label={t('s.configuracao.alarme.emailsalarmeconfig')}
                rows={6}
              />
            )}

            {isVisible && (
              <>
                {![ALARME_VAZAONAOAUTORIZADA, ALARME_PAUSAS_DBIC].includes(
                  initials?.idalarmeconfig
                ) ? (
                  <Select
                    name="tipocalculoalarmeconfig"
                    label={t('s.configuracao.alarme.tipocalculoalarmeconfig')}
                    options={optionsVP}
                  />
                ) : (
                  <Hidden
                    name="tipocalculoalarmeconfig"
                    value={
                      initials.idalarmeconfig === ALARME_PAUSAS_DBIC
                        ? 'V'
                        : initials.tipocalculoalarmeconfig
                    }
                  />
                )}
                <Input
                  name="valorabsolutoalarmeconfig"
                  label={
                    initials.idalarmeconfig === ALARME_PAUSAS_DBIC
                      ? t('s.geral.pausasdbicmovimentacaoabastecimento')
                      : t('s.configuracao.alarme.valorabsolutoalarmeconfig')
                  }
                  type="number"
                />
              </>
            )}
            <ExpansionPanel expanded>
              <ExpansionPanelSummary
                aria-controls="panel3a-content"
                id="panel3a-header"
                style={{
                  background: 'rgba(0, 0, 0, 0.05)',
                  borderBottom: '1px solid rgba(0,0,0,.2)',
                }}
              >
                <Typography>{t('Alarme Usuários')}</Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <S.Container>
                  <CheckboxGroup
                    search
                    name="listaidsusuariosalarmeconfigcliente"
                    title={t('Usuarios')}
                    label="nomeusuario"
                    unique="idusuario"
                    url="usuariosoperadores/?filteruser=false"
                    getValue={(value) => setUserPosto(value)}
                    concat="codigousuario"
                    query={{
                      wherefield: ['tipousuario'],
                      valuewherefield: ['F'],
                      typewherefield: ['NEQUALS'],
                      pesquisalista: ['true'],
                      orderfield: ['nomeusuario'],
                      ordination: ['asc'],
                    }}
                    onLoadingChange={handleAsyncLoadingChange}
                    onLoadingError={handleAsyncLoadingError}
                  />
                </S.Container>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          </Form>
          <Spinner loading={pendingRequests > 0} />
        </div>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func,
  toggleConfirmButton: PropTypes.func,
};

CreateOrUpdate.defaultProps = {
  data: {},
  toggleLoading: () => {},
  toggleConfirmButton: () => {},
};
